import React from "react";
import MuiCard from "@mui/material/Card";
import MuiCardContent from "@mui/material/CardContent";
import MuiButton from "@mui/material/Button";
import MuiGrid from "@mui/material/Grid";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";

// Card - matches material-dashboard Card (styled div with shadow/radius)
export function Card({ children, className, style, ...rest }) {
  return (
    <MuiCard
      className={className}
      style={style}
      sx={{
        boxShadow: "0 1px 4px 0 rgba(0, 0, 0, 0.14)",
        borderRadius: "6px",
        width: "100%",
        paddingTop: "30px",
        paddingBottom: "30px",
        display: "flex",
        flexDirection: "column",
        minWidth: "0",
        overflow: "visible",
      }}
      {...rest}
    >
      {children}
    </MuiCard>
  );
}

// CardBody - matches material-dashboard CardBody (content wrapper)
export function CardBody({ children, className, style, ...rest }) {
  return (
    <MuiCardContent
      className={className}
      style={style}
      sx={{ padding: "0.9375rem 20px", "&:last-child": { paddingBottom: "0.9375rem" } }}
      {...rest}
    >
      {children}
    </MuiCardContent>
  );
}

// Button - matches material-dashboard Button
export function Button({ children, className, color, style, ...rest }) {
  return (
    <MuiButton
      className={className}
      color={color === "primary" ? "primary" : "inherit"}
      variant="contained"
      style={style}
      sx={{
        color: "#fff",
        padding: "12px 30px",
        borderRadius: "3px",
        fontSize: "12px",
        fontWeight: 400,
        lineHeight: "1.42857143",
        letterSpacing: "0",
        boxShadow:
          "0 2px 2px 0 rgba(153, 153, 153, 0.14), 0 3px 1px -2px rgba(153, 153, 153, 0.2), 0 1px 5px 0 rgba(153, 153, 153, 0.12)",
        "&.Mui-disabled": {
          opacity: 0.65,
          color: "#fff",
          backgroundColor: "inherit",
        },
      }}
      {...rest}
    >
      {children}
    </MuiButton>
  );
}

// GridContainer - matches material-dashboard GridContainer
export function GridContainer({ children, className, style, ...rest }) {
  return (
    <MuiGrid container className={className} style={style} {...rest}>
      {children}
    </MuiGrid>
  );
}

// GridItem - matches material-dashboard GridItem (with 15px padding)
export function GridItem({ children, className, noPadding, style, ...rest }) {
  return (
    <MuiGrid
      item
      className={className}
      style={style}
      sx={{ padding: noPadding ? "0 !important" : "0 15px !important" }}
      {...rest}
    >
      {children}
    </MuiGrid>
  );
}

// GridContainerWithSpacing - responsive spacing variant
// MUI v4 used symmetric padding (all sides) on Grid items. MUI v5 uses
// asymmetric (left/top only), which breaks layouts that relied on symmetric gaps.
// We skip MUI's spacing system entirely and apply padding manually to match v4.
export function GridContainerWithSpacing({ children, className, style, ...rest }) {
  return (
    <MuiGrid
      container
      className={className}
      style={{ marginTop: "0px", marginBottom: "0px", ...style }}
      {...rest}
    >
      {children}
    </MuiGrid>
  );
}

// GridItemWithSpacing - applies symmetric horizontal padding to match MUI v4
export function GridItemWithSpacing({ children, className, style, ...rest }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  // MUI v4 spacing={4} → 16px per side, spacing={10} → 40px per side
  const pad = isMobile ? theme.spacing(2) : theme.spacing(5);

  return (
    <MuiGrid
      item
      className={className}
      style={style}
      sx={{
        paddingLeft: `${pad} !important`,
        paddingRight: `${pad} !important`,
        paddingTop: "0 !important",
        paddingBottom: "0 !important",
      }}
      {...rest}
    >
      {children}
    </MuiGrid>
  );
}
